import { DatePicker, Flex, Table } from 'antd'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useTreatmentServiceGetTreatmentPlanListEndpoint } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  TreatmentPlanSortBy
} from 'src/api/requests'
import { Select, Text, ClassifierInfiniteSelect } from 'src/components'
import {
  useTreatmentColumns,
  useTreatmentConfig
} from 'src/features/persons/components/PersonTreatmentsSection'
import { useStyles } from 'src/features/persons/styles'
import { useClassifierId, useTablePagination } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

export const PersonTreatmentPage = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { id: personId } = useParams()

  const pagination = useTablePagination()

  const { options } = useTreatmentConfig()
  const { columns } = useTreatmentColumns()

  const [treatmentType, setTreatmentType] = useState('')
  const [date, setDate] = useState<string[]>([])
  const [ordering, setOrdering] = useState('')

  const classifierTreatmentType = useClassifierId(
    ClassifierType.TREATMENT_TYPE,
    ClassifierCategory.TREATMENT
  )

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as TreatmentPlanSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(treatmentType && { treatmentType }),
    ...(date?.[0] && date?.[1] && { startDate: date?.[0], endDate: date?.[1] }),
    personId
  }

  const { data, isLoading } =
    useTreatmentServiceGetTreatmentPlanListEndpoint(queryParams)

  const onDateChange = (datesString: string[]) => {
    setDate(datesString)
    resetPagination(pagination)
  }

  const onSelect = (val: string) => {
    setOrdering(val)
    resetPagination(pagination)
  }

  const onTreatmentSelect = (val: { label: string }) => {
    setTreatmentType(val.label)
    resetPagination(pagination)
  }

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" wrap="wrap" gap={8}>
        <Text className={styles.text}>{t('menu.treatments')}</Text>

        <Flex gap={8}>
          <DatePicker.RangePicker
            format={[dayMonthYearBackSlash, dayMonthYearBackSlash]}
            onChange={(_, datesString) => onDateChange(datesString)}
            placeholder={[
              t('notifications.from_date'),
              t('notifications.to_date')
            ]}
          />

          <ClassifierInfiniteSelect
            labelInValue
            onSelect={onTreatmentSelect}
            onClear={() => setTreatmentType('')}
            id={classifierTreatmentType}
            placeholder={t('treatments.treatment_type')}
            style={{
              minWidth: 200
            }}
          />

          <Select onSelect={onSelect} options={options} />
        </Flex>
      </Flex>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </Flex>
  )
}
